import { t as uiText } from '../../i18n';
/**
 * Yjsフィールドのキー名を一元管理し、型の競合を防ぐユーティリティ（安全版）
 * 
 * @deprecated ensureMetaMap(ydoc, metaKey, opts) - 代わりにensureMetaMapByBoardId(ydoc, boardId, opts)を使用
 * @recommended ensureMetaMapByBoardId() - boardIdベースの安全なAPI
 */
import * as Y from 'yjs';

export class YjsKeyManager {
  private static readonly CONTENT_PREFIX = 'content';
  private static readonly META_PREFIX = 'meta';
  private static readonly AWARENESS_PREFIX = 'awareness';

  // 本文用のキー名
  static getContentKey(boardId: string): string {
    return `${this.CONTENT_PREFIX}_${boardId}`;
  }

  // メタ情報用のキー名
  static getMetaKey(boardId: string): string {
    // バージョン付きメタキーに移行して、過去の壊れた型を回避
    return `${this.META_PREFIX}2_${boardId}`;
  }

  // アウェアネス用のキー名
  static getAwarenessKey(boardId: string): string {
    return `${this.AWARENESS_PREFIX}_${boardId}`;
  }

  /**
   * 既存フィールドの型を安全にチェック
   */
  static checkFieldType(
    ydoc: any,
    key: string,
    expectedType: string,
    opts?: { warn?: boolean }
  ): boolean {
    try {
      if (!ydoc.share.has(key)) return false;

      const field = ydoc.share.get(key);
      const actualType = this.getFieldType(field);

      if (actualType !== expectedType) {
        if (opts?.warn ?? true) {
          console.warn(
            `⚠️ フィールド型の不一致: ${key} (期待: ${expectedType}, 実際: ${actualType})`
          );
        }
        return false;
      }
      return true;
    } catch (error) {
      console.error(`❌ フィールド型チェックエラー: ${key}`, error);
      return false;
    }
  }

  /**
   * フィールドの実際の型を判定（バンドル混在に強い）
   */
  private static getFieldType(field: any): string {
    if (!field) return 'undefined';

    try {
      // constructor.name 優先
      const ctorName = (field as any)?.constructor?.name;
      if (ctorName === 'YMap' || ctorName === 'Map') return 'map';
      if (ctorName === 'YArray' || ctorName === 'Array') return 'array';
      if (ctorName === 'YText' || ctorName === 'Text') return 'text';
      if (ctorName === 'XmlFragment') return 'xmlfragment';
      if (ctorName === 'XmlText') return 'xmltext';
    } catch (_) {}

    try {
      // duck-typing
      const has = (name: string) => typeof (field as any)[name] === 'function';
      if (has('set') && has('get') && has('delete')) return 'map';
      if (has('insert') && has('delete') && has('push')) return 'array';
      if (has('insert') && has('delete') && !has('push') && !has('set'))
        return 'text';
    } catch (_) {}

    try {
      // 最後に instanceof
      if (field instanceof Y.Map) return 'map';
      if (field instanceof Y.Array) return 'array';
      if (field instanceof Y.Text) return 'text';
      const YAny: any = Y as any;
      if (YAny.XmlFragment && field instanceof YAny.XmlFragment)
        return 'xmlfragment';
      if (YAny.XmlText && field instanceof YAny.XmlText) return 'xmltext';
    } catch (_) {}

    return 'unknown';
  }

  /**
   * 安全にフィールドを取得または作成
   * - 型不一致でも削除はせず既存フィールドを返す
   */
  static createFieldSafely(
    ydoc: any,
    key: string,
    fieldType: 'map' | 'xmlfragment' | 'array',
    opts?: { forceRecreate?: boolean }
  ): any {
    // React Strict Mode対応：重複呼び出しをキャッシュで制御
    const cacheKey = `_cache_${key}_${fieldType}`;
    const cachedResult = (ydoc as any)[cacheKey];
    
    if (cachedResult && !opts?.forceRecreate) {
          const enableDetailedLogs = import.meta.env.MODE === 'development' && import.meta.env.VITE_DEBUG_EDITOR === 'true';
    if (enableDetailedLogs) {}
      return cachedResult;
    }
    
    if (import.meta.env.MODE === 'development') {}
    
    try {
      if (this.checkFieldType(ydoc, key, fieldType, { warn: false })) {
        if (import.meta.env.MODE === 'development') {}
        const result = ydoc.share.get(key);
        // 結果をキャッシュ
        (ydoc as any)[cacheKey] = result;
        return result;
      }

      if (ydoc.share.has(key)) {
        // meta_ または content 系キー かつ 強制再作成オプションが true の場合のみ削除
        const isMetaKey =
          typeof key === 'string' && key.startsWith(`${this.META_PREFIX}_`);
        const isContentKey =
          typeof key === 'string' && (key === this.CONTENT_PREFIX || key.startsWith(`${this.CONTENT_PREFIX}_`));
        if ((isMetaKey || isContentKey) && opts?.forceRecreate) {
          console.warn(`⚠️ ${key} を再作成します（forceRecreate=true）`);
          let recreated: any = null;
          Y.transact(ydoc, () => {
            try {
              ydoc.share.delete(key);
            } catch (_) {}
            recreated = this.instantiateField(ydoc, key, fieldType);
          });
          return recreated;
        }
        const result = ydoc.share.get(key);
        // 結果をキャッシュ
        (ydoc as any)[cacheKey] = result;
        return result;
      }

      // 新規作成
      if (import.meta.env.MODE === 'development') {}
      const newField = this.instantiateField(ydoc, key, fieldType);
      if (import.meta.env.MODE === 'development') {}
      // 結果をキャッシュ
      (ydoc as any)[cacheKey] = newField;
      return newField;
    } catch (error) {
      console.error(`❌ フィールド作成エラー: ${key} (${fieldType})`, error);
      return null;
    }
  }

  /**
   * 実際に Yjs フィールドを生成
   */
  private static instantiateField(
    ydoc: any,
    key: string,
    fieldType: 'map' | 'xmlfragment' | 'array'
  ) {
    switch (fieldType) {
      case 'map':
        return ydoc.getMap(key);
      case 'xmlfragment':
        return ydoc.getXmlFragment(key);
      case 'array':
        return ydoc.getArray(key);
      default:
        throw new Error(uiText("Unsupported field type: {{fieldType}}", { fieldType: fieldType }));
    }
  }

  /**
   * 利用可能なMapフィールドを検索
   */
  static findAvailableMapField(
    ydoc: any
  ): { field: any; key: string } | null {
    try {
      const keys = Array.from(ydoc.share.keys()) as string[];
      const metaKeys = keys.filter(
        (key) =>
          typeof key === 'string' && key.startsWith(`${this.META_PREFIX}_`)
      );

      // タイトル/スラッグ付き優先
      for (const key of metaKeys) {
        if (this.checkFieldType(ydoc, key, 'map', { warn: false })) {
          const field = ydoc.share.get(key);
          if (field.has('title') || field.has('slug')) {
            return { field, key };
          }
        }
      }

      // それ以外
      for (const key of metaKeys) {
        if (this.checkFieldType(ydoc, key, 'map', { warn: false })) {
          const field = ydoc.share.get(key);
          return { field, key };
        }
      }

      return null;
    } catch (error) {
      console.error('❌ Mapフィールド検索エラー:', error);
      return null;
    }
  }

  /**
   * meta用のキーに対して、必ず Y.Map を返すことを目標に安全に確保する
   * - 既存が別型の場合、overwriteOnConflict が true なら削除して再生成
   * - 戻り値の map が必ず Y.Map であることはここで最大限努力するが、
   *   環境差異により instanceof 判定が不安定な場合もあるため、呼び出し側で最終保証してもよい
   * 
   * @private 内部実装用。外部からはensureMetaMapByBoardIdを使用すること
   */
  private static ensureMetaMap(
    ydoc: any,
    metaKey: string,
    opts?: { warn?: boolean; overwriteOnConflict?: boolean }
  ): { key: string; map: any } {
    const warn = opts?.warn ?? false;
    try {
      // 既存がある場合
      if (ydoc.share.has(metaKey)) {
        let field = ydoc.share.get(metaKey);

        // 既に Y.Map の場合はそのまま返却
        if (field instanceof Y.Map) {
          return { key: metaKey, map: field };
        }

        // 別型だが上書き許可がある場合は再生成
        if (opts?.overwriteOnConflict) {
          if (warn) {
            console.warn(`⚠️ metaキーの型競合を検出し再生成します: ${metaKey}`);
          }
          Y.transact(ydoc, () => {
            try {
              ydoc.share.delete(metaKey);
            } catch (_) {}
            field = ydoc.getMap(metaKey);
          });
          return { key: metaKey, map: field };
        }

        // 上書きできない場合はそのまま返す（呼び出し側で最終保証）
        return { key: metaKey, map: field };
      }

      // 存在しない場合は新規作成
      const created = ydoc.getMap(metaKey);
      return { key: metaKey, map: created };
    } catch (error) {
      console.error('❌ ensureMetaMap エラー:', error);
      // 失敗時はそのまま新規生成を試みる
      try {
        const created = ydoc.getMap(metaKey);
        return { key: metaKey, map: created };
      } catch (e) {
        console.error('❌ ensureMetaMap 代替生成エラー:', e);
        return { key: metaKey, map: null };
      }
    }
  }

  /**
   * 与えられたフィールド値が既知のYjs型かどうかを判定
   */
  static isRecognizedYType(field: any): boolean {
    const t = this.getFieldType(field);
    return (
      t === 'map' ||
      t === 'array' ||
      t === 'text' ||
      t === 'xmlfragment' ||
      t === 'xmltext'
    );
  }

  /**
   * ydoc.share に格納された rootType を走査し、Yjs が理解できない型を削除
   * - 例: plain object, null, undefined など
   * - 削除は transact 内で安全に行う
   */
  static sanitizeShareRootTypes(
    ydoc: any,
    opts?: { log?: boolean }
  ): void {
    try {
      const keys = Array.from(ydoc.share.keys()) as string[];
      if (!keys.length) return;

      Y.transact(ydoc, () => {
        for (const key of keys) {
          let field: any = null;
          try {
            field = ydoc.share.get(key);
          } catch (_) {}

          if (!this.isRecognizedYType(field)) {
            if (opts?.log) {
              console.warn(`⚠️ 不正なrootTypeを削除: ${key} (type=${this.getFieldType(field)})`);
            }
            try {
              ydoc.share.delete(key);
            } catch (e) {
              console.error(`❌ rootType削除エラー: ${key}`, e);
            }
          }
        }
      });
    } catch (error) {
      console.error('❌ sanitizeShareRootTypes エラー:', error);
    }
  }

  /**
   * 旧メタキー（meta_ プレフィックス、ただし meta2_ は除外）を物理削除
   */
  static removeLegacyMetaKeys(ydoc: any, opts?: { log?: boolean }): boolean {
    try {
      const keys = Array.from(ydoc.share.keys()) as string[];
      let removedAny = false;
      Y.transact(ydoc, () => {
        for (const key of keys) {
          if (typeof key === 'string' && key.startsWith('meta_') && !key.startsWith('meta2_')) {
            if (opts?.log) {
              console.warn(`🧹 旧メタキーを削除: ${key}`);
            }
            try { ydoc.share.delete(key); removedAny = true; } catch (_) {}
          }
        }
      });
      return removedAny;
    } catch (error) {
      console.error('❌ 旧メタキー削除エラー:', error);
      return false;
    }
  }

  /**
   * コンテンツフィールドをXmlFragmentとして確保（グローバル固定キー）
   */
  static ensureContentXmlFragment(ydoc: any): any {
    try {
      // content キーは常にグローバル固定（既存システムとの互換性のため）
      const contentKey = 'content';
      
      // 既存のcontentフィールドをチェック
      const existingField = ydoc.share.get(contentKey);
      
      // NOTE:
      // - production build では chunk 分割等で Yjs が複数インスタンス扱いになり、
      //   `instanceof Y.XmlFragment` が偽になるケースがある（その場合 forceRecreate が走り本文が消える）。
      // - そのため ctorName ベース + 互換ダックタイピングで XmlFragment を判定する。
      const ctorName = existingField?.constructor?.name;
      const looksLikeXmlFragment =
        ctorName === 'XmlFragment' ||
        ctorName === 'YXmlFragment' ||
        (existingField &&
          typeof existingField.insert === 'function' &&
          typeof existingField.delete === 'function' &&
          typeof existingField.toString === 'function' &&
          // XmlFragment には toArray がある（Text などと区別しやすい）
          typeof existingField.toArray === 'function');

      if (looksLikeXmlFragment) {
        // 既に正しい型（または互換のXmlFragment）が存在する場合はそのまま返す
        return existingField;
      }
      
      // 強制的にXmlFragmentを作成
      return this.createFieldSafely(ydoc, contentKey, 'xmlfragment', {
        forceRecreate: true
      });
    } catch (error) {
      console.error('❌ コンテンツフィールド確保エラー:', error);
      return null;
    }
  }

  /**
   * 厳格な型固定処理（統一された呼び出し順序）
   * 順序: removeLegacyMetaKeys → ensureContentXmlFragment → ensureMetaMap → sanitizeShareRootTypes → ensureMetaMap（最終保証）
   */
  static enforceStrictTyping(ydoc: any, boardId: string, opts?: { log?: boolean }): void {
    if (!ydoc || !boardId) {
      console.warn('⚠️ enforceStrictTyping: 無効な引数', { ydoc: !!ydoc, boardId });
      return;
    }

    try {
      // DIAG: 開始ログ（初期化由来のupdate切り分け用）
      try {
        const preKeys = Array.from(ydoc?.share?.keys?.() || []);
      } catch (_) {}

      Y.transact(ydoc, () => {
        // 1. 旧メタキー削除
        this.removeLegacyMetaKeys(ydoc, { log: opts?.log ?? true });
        
        // 2. コンテンツフィールド確保（XmlFragment、グローバル固定）
        this.ensureContentXmlFragment(ydoc);
        
        // 3. メタマップ確保（初回）
        this.ensureMetaMapByBoardId(ydoc, boardId, { overwriteOnConflict: true });
        
        // 4. 不正データ削除
        this.sanitizeShareRootTypes(ydoc, { log: opts?.log ?? true });
        
        // 5. メタマップ最終保証
        this.ensureMetaMapByBoardId(ydoc, boardId, { overwriteOnConflict: true });
      });

      if (opts?.log) {}

      // DIAG: 終了ログ
      try {
        const postKeys = Array.from(ydoc?.share?.keys?.() || []);
      } catch (_) {}
    } catch (error) {
      console.error('❌ enforceStrictTyping エラー:', error);
    }
  }

  /**
   * boardIdを受け取ってメタマップを確保する安全なAPI
   * - 内部でmetaKeyを自動生成するため、キー名の誤りを防止
   * - 推奨：外部からはこのメソッドを使用してください
   */
  static ensureMetaMapByBoardId(
    ydoc: any,
    boardId: string,
    opts?: { warn?: boolean; overwriteOnConflict?: boolean }
  ): { key: string; map: any } {
    const metaKey = this.getMetaKey(boardId);
    return this.ensureMetaMap(ydoc, metaKey, opts);
  }
}
